import React, { useState, useEffect } from 'react';
import { ScrollText, ChevronDown, ChevronUp, ChevronRight, User, Clock, CheckCircle, XCircle, Pause, AlertTriangle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_STYLES = {
    active: { icon: Clock, color: 'text-blue-500', bg: 'bg-blue-50', label: 'Active' },
    completed: { icon: CheckCircle, color: 'text-emerald-500', bg: 'bg-emerald-50', label: 'Completed' },
    failed: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50', label: 'Failed' },
    manually_exited: { icon: Pause, color: 'text-stone-400', bg: 'bg-stone-50', label: 'Exited' },
    paused: { icon: Pause, color: 'text-amber-500', bg: 'bg-amber-50', label: 'Paused' },
};

function StatusBadge({ status }) {
    const s = STATUS_STYLES[status] || STATUS_STYLES.active;
    const Icon = s.icon;
    return (
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${s.color} ${s.bg}`}>
            <Icon className="w-3 h-3" /> {s.label}
        </span>
    );
}

function EnrollmentRow({ enrollment }) {
    const [expanded, setExpanded] = useState(false);

    return (
        <div className="border border-stone-200 rounded-lg overflow-hidden">
            <button onClick={() => setExpanded(!expanded)}
                className="w-full px-3 py-2 flex items-center gap-2 hover:bg-stone-50 transition-colors text-left">
                <ChevronRight className={`w-3.5 h-3.5 text-stone-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
                <User className="w-3.5 h-3.5 text-stone-400" />
                <div className="flex-1 min-w-0">
                    <span className="text-xs font-medium text-stone-700 truncate block">
                        {enrollment.contact_name || 'Unknown'}
                    </span>
                    <span className="text-[10px] text-stone-400">{enrollment.contact_email}</span>
                </div>
                <StatusBadge status={enrollment.status} />
                {enrollment.current_step_label && (
                    <span className="text-[10px] text-stone-400 hidden sm:block">
                        @ {enrollment.current_step_label}
                    </span>
                )}
                {enrollment.enrolled_at && (
                    <span className="text-[10px] text-stone-400">
                        {formatDistanceToNow(new Date(enrollment.enrolled_at), { addSuffix: true })}
                    </span>
                )}
            </button>

            {expanded && (
                <div className="px-3 pb-3 pt-1 bg-stone-50 border-t border-stone-100">
                    {enrollment.ab_split_group && (
                        <div className="text-[10px] text-purple-600 mb-2">
                            A/B Group: <span className="font-semibold">{enrollment.ab_split_group}</span>
                        </div>
                    )}
                    {enrollment.goal_met && (
                        <div className="text-[10px] text-emerald-600 mb-2 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> Goal achieved
                        </div>
                    )}

                    {/* Step history timeline */}
                    <div className="space-y-0">
                        {(enrollment.step_history || []).map((sh, i) => (
                            <div key={i} className="flex items-start gap-2 py-1">
                                <div className="flex flex-col items-center">
                                    <div className={`w-2 h-2 rounded-full mt-1 ${
                                        sh.outcome === 'sent' || sh.outcome === 'completed' || sh.outcome === 'branched'
                                            ? 'bg-emerald-400'
                                            : sh.outcome === 'failed'
                                                ? 'bg-red-400'
                                                : sh.outcome === 'waiting' || sh.outcome === 'timed_out'
                                                    ? 'bg-amber-400'
                                                    : 'bg-stone-300'
                                    }`} />
                                    {i < (enrollment.step_history || []).length - 1 && (
                                        <div className="w-px h-4 bg-stone-200" />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[11px] font-medium text-stone-700">
                                        {sh.step_label || sh.step_type}
                                    </div>
                                    <div className="text-[10px] text-stone-400 flex items-center gap-2">
                                        {sh.entered_at && (
                                            <span>{new Date(sh.entered_at).toLocaleString()}</span>
                                        )}
                                        {sh.outcome && (
                                            <span className={`font-medium ${
                                                sh.outcome === 'failed' ? 'text-red-500' :
                                                sh.outcome === 'timed_out' ? 'text-amber-500' : 'text-stone-500'
                                            }`}>
                                                {sh.outcome}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default function ExecutionLogViewer({ campaignId }) {
    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        if (!campaignId) return;
        setLoading(true);
        const token = localStorage.getItem('crm_token');
        fetch(`${API}/api/campaigns/${campaignId}/execution-logs?page=${page}&page_size=20`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(r => r.json())
            .then(data => {
                setLogs(data.data || []);
                setTotal(data.total || 0);
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [campaignId, page]);

    return (
        <div className="bg-white border-b border-stone-200">
            <button onClick={() => setExpanded(!expanded)}
                className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-stone-50 transition-colors">
                <div className="flex items-center gap-2">
                    <ScrollText className="w-4 h-4 text-indigo-500" />
                    <span className="text-sm font-medium text-stone-700">Execution Logs</span>
                    <span className="text-xs text-stone-400">({total})</span>
                </div>
                {expanded ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
            </button>

            {expanded && (
                <div className="px-4 pb-3 space-y-2">
                    {loading ? (
                        <p className="text-xs text-stone-400 py-2">Loading logs...</p>
                    ) : logs.length === 0 ? (
                        <p className="text-xs text-stone-400 py-2">No enrollments yet.</p>
                    ) : (
                        <>
                            {logs.map(e => <EnrollmentRow key={e.enrollment_id} enrollment={e} />)}

                            {/* Pagination */}
                            {total > 20 && (
                                <div className="flex items-center justify-between pt-2">
                                    <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                                        className="text-xs text-stone-500 hover:text-stone-700 disabled:opacity-30">
                                        ← Previous
                                    </button>
                                    <span className="text-[10px] text-stone-400">
                                        Page {page} of {Math.ceil(total / 20)}
                                    </span>
                                    <button disabled={page >= Math.ceil(total / 20)} onClick={() => setPage(p => p + 1)}
                                        className="text-xs text-stone-500 hover:text-stone-700 disabled:opacity-30">
                                        Next →
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
